import { fonts, fontSize } from "config/styles/fonts";
import { colors } from "config/styles/themeColors";
import { shadows } from "config/styles/shadows";
import { IAccentColors } from "config/types";

const styles = {
  contentWrap: {
    height: "calc(100dvh - 40px)",
    p: {
      xs: "20px 16px",
      md: "20px 36px",
    },
  },
  headerWrap: {
    display: "flex",
    alignItems: "center",
    "& .MuiButtonBase-root.MuiIconButton-root": {
      display: "block",
      p: "12px 20px 8px 0",
      ml: {
        xs: 0,
        md: "-4px",
      },
      "&:hover": {
        backgroundColor: "unset",
      },
      "& svg": {
        fill: colors.text.primary,
      },
    },
  },
  title: {
    fontSize: {
      xs: fontSize.l,
      md: fontSize.xl,
    },
    fontWeight: 1000,
  },
  radioGroup: {
    width: "100%",
    ml: 0,
    "& .MuiFormControl-root": {
      width: "100%",
      mt: "20px",
      ml: "8px",
    },
    "& .MuiFormGroup-root": {
      flexWrap: "nowrap",
      flexDirection: "column",
      gap: "20px",
    },
  },
  dateTimeWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
    pt: "20px",
  },
  datePickerMain: (accentColor: IAccentColors) => ({
    width: {
      xs: "100%",
      lg: "calc(50% - 10px)",
    },
    "& .react-datepicker-wrapper": {
      width: "100%",
      "& .react-datepicker__input-container": {
        display: "flex",
        flexDirection: "row-reverse",
        "& .react-datepicker__calendar-icon": {
          cursor: "pointer",
          m: "8px 16px",
          p: 0,
          fill: colors.text.secondary,
        },
      },
    },
    "& .react-datepicker-wrapper input": {
      width: "100%",
      height: "40px",
      padding: "10px 16px",
      borderRadius: "4px",
      border: "1px solid",
      borderColor: "gray.disabled",
      color: "text.primary",
      lineHeight: "normal",
      fontFamily: fonts.text,
      fontWeight: 400,
      fontSize: fontSize.m,
      "&:focus-visible": {
        outline: "none",
        border: "2px solid",
        borderColor: "text.secondary",
      },
      "&::placeholder": {
        color: "gray.disabled",
      },
    },
    "& .datepicker-container": {
      fontFamily: fonts.text,
      fontWeight: 700,
      fontSize: fontSize.m,
      lineHeight: "16px",
      maxWidth: "fit-content",
      p: "12px 11px 5px",
    },
    "& .react-datepicker": {
      border: "none",
      boxShadow: shadows.main,
      borderRadius: "10px",
    },
    "& .react-datepicker__current-month": {
      color: "text.primary",
      mb: "8px",
      fontSize: fontSize.m,
      fontWeight: 700,
    },
    "& .react-datepicker__day-names": {
      fontWeight: 600,
      lineHeight: "20px",
    },
    "& .react-datepicker__week": {
      display: "flex",
      fontWeight: 300,
      lineHeight: "23.17px",
      fontSize: fontSize.m,
    },
    "& .react-datepicker__week:not(:last-child)": {
      mb: "8px",
    },
    "& .react-datepicker__day": {
      position: "relative",
      pt: "5px",
      width: "38px !important",
      height: "34px !important",
      margin: "0 !important",
      borderRadius: "4px",
      color: "text.primary",
      "&:hover": {
        backgroundColor: accentColor.hover || "accents.hover",
      },
    },
    "& .react-datepicker__day--today": {
      position: "relative",
      pt: "4px",
      width: "38px !important",
      height: "34px !important",
      margin: "0 !important",
      border: "solid 1px",
      borderColor: accentColor.main || "accents.main",
      borderRadius: "4px !important",
    },
    "& .react-datepicker__day-name": {
      width: "38px !important",
      height: "34px !important",
      margin: "0 !important",
      borderRadius: "4px",
      color: "text.primary",
    },
    "& .react-datepicker__header": {
      backgroundColor: "background.default",
      borderBottom: "none",
    },
    "& .react-datepicker__day:hover": {
      backgroundColor: accentColor.hover || "accents.hover",
      color: "text.primary",
    },
    "& .react-datepicker__day--outside-month": {
      visibility: "hidden",
    },
    "& .react-datepicker__triangle": {
      display: "none",
    },
    "& .react-datepicker__navigation": {
      top: "4px",
    },
    "& .react-datepicker__navigation-icon--next::before, .react-datepicker__navigation-icon--previous::before":
      {
        height: "9px",
        width: "9px",
        borderColor: "text.primary",
        borderWidth: "1.5px 1.5px 0 0",
      },
    "& .react-datepicker__month": {
      mb: "16px",
    },
    "& .chosen-date": {
      backgroundColor: "gray.tagDisabled",
      fontWeight: 700,
    },
    "& .react-datepicker__day--keyboard-selected, .react-datepicker__day--selected, .react-datepicker__day--in-range":
      {
        position: "relative",
        backgroundColor: accentColor.main || "accents.main",
        color: "background.default",
        outline: "none",
      },
    "& .react-datepicker__day--selected: hover": {
      backgroundColor: accentColor.main || "accents.main",
    },
    "& .react-datepicker__day--disabled": {
      color: `${colors.gray.disabled} !important`,
      "&:hover": {
        backgroundColor: `${colors.background.default} !important`,
      },
    },
  }),
  datePicker: (accentColor: IAccentColors) => ({
    width: "100%",
    "& .react-datepicker__tab-loop .react-datepicker-popper": {
      "& .react-datepicker__time": {
        borderRadius: "10px",
      },
    },
    "& .react-datepicker__time-list-item:hover": {
      backgroundColor:
        `${accentColor.hover} !important` ||
        `${colors.accents.hover} !important`,
      "&.react-datepicker__time-list-item--disabled:hover": {
        backgroundColor: `${colors.background.default} !important`,
      },
    },
    "& .react-datepicker__time-list-item--selected": {
      backgroundColor:
        `${accentColor.main} !important` || `${colors.accents.main} !important`,
    },
  }),
  timePicker: {
    "& .react-datepicker": {
      fontSize: fontSize.m,
      border: "none",
      color: colors.text.primary,
      boxShadow: shadows.main,
      borderRadius: "10px",
    },
    "& .react-datepicker__tab-loop .react-datepicker-popper": {
      width: "calc(100% - 72px)",
      height: "240px",
      "& .react-datepicker.react-datepicker--time-only": {
        width: "100%",
        height: "100%",
        "& .react-datepicker__time-container": {
          width: "100%",
          height: "100%",
          "& .react-datepicker__time": {
            height: "100%",
            "& .react-datepicker__time-box": {
              width: "100%",
              height: "100%",
              "& .react-datepicker__time-list": {
                height: "100%",
                "& .react-datepicker__time-list-item": {
                  p: "10px",
                  height: "40px",
                },
              },
            },
          },
        },
      },
    },
  },
  dateTimeLabel: {
    width: "fit-content",
    color: "gray.main",
  },
  btn: {
    width: {
      xs: "calc(100% - 32px)",
      md: "calc(100% - 72px)",
    },
    height: "56px",
    fontSize: fontSize.l,
    textTransform: "capitalize",
    borderRadius: "50px",
    position: "absolute",
    bottom: "20px",
  },
};

export default styles;
